/**
 * Landing template: "Asamika Abode" style — deep forest-green full-bleed
 * sections, serif display headings, dotted red rules, red/maroon CTAs,
 * a glass availability panel over the hero, dark room cards with pill
 * amenity tags. Modelled on the reference site the user named
 * (asamikaabode.com).
 *
 * Uses Tailwind's built-in `font-serif` stack rather than pulling in a
 * Google Font — the classic template already relies on the same stack, and
 * a new webfont would add a network dependency (and CSP surface) for a
 * cosmetic gain.
 *
 * Same live tenant data and same honest empty states as every other
 * template — no fabricated placeholder content anywhere.
 */
import React, { useState, useEffect } from "react";
import {
  MapPin, Phone, Mail, Star, Menu, X, BedDouble, Users, ArrowRight,
  ChevronDown, ExternalLink, MessageCircle, ArrowUp,
} from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { toLocalISODate } from "../../utils/date";
import { imgSrc, useLandingData, RoomPhotoSlideshow, StarRow, ReviewLookupModal } from "./shared";

const NAV_LINKS = [["About", "about"], ["Rooms", "rooms"], ["Amenities", "amenities"], ["Gallery", "gallery"], ["Contact", "contact"]];

const GREEN = "#0F2A1D";
const GREEN_SOFT = "#163A28";
const RED = "#B91C1C";

// The signature thin dotted red rule that separates every band.
function DottedRule({ className = "" }) {
  return (
    <div className={`h-[3px] w-full ${className}`}
      style={{ backgroundImage: `repeating-linear-gradient(to right, ${RED} 0 10px, transparent 10px 20px)` }} />
  );
}

function Eyebrow({ children, light = false }) {
  return (
    <p className={`flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.28em] ${light ? "text-white/60" : "text-stone-500"}`}>
      <span className="inline-block w-8 h-[2px]" style={{ backgroundImage: `repeating-linear-gradient(to right, ${RED} 0 5px, transparent 5px 10px)` }} />
      {children}
    </p>
  );
}

export default function LandingPageAsamika() {
  const {
    hotelName, address, phone, phone2, email, heroImages, aboutText, faqs,
    mapsUrl, mapsLat, mapsLng, crossPromoName, crossPromoUrl, googleReviewUrl, logoUrl,
  } = useSettings();
  const { rooms, bookingDisabled, liveReviews } = useLandingData();

  const [menuOpen, setMenuOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [showReviewLookup, setShowReviewLookup] = useState(false);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(2);
  const [showTop, setShowTop] = useState(false);

  const gallery = (heroImages || []).map(imgSrc).filter(Boolean);
  const heroPhoto = gallery[0] || null;

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const avgRating = liveReviews.length
    ? (liveReviews.reduce((s, r) => s + (r.rating || 0), 0) / liveReviews.length).toFixed(1)
    : null;

  const bookHref = checkIn && checkOut
    ? `/book?checkIn=${checkIn}&checkOut=${checkOut}&guestCount=${guests}`
    : "/book";

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const waHref = phone ? `https://wa.me/${String(phone).replace(/\D/g, "")}` : null;

  return (
    <div className="min-h-screen bg-[#F6F3EC] text-stone-900">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-50 bg-[#F6F3EC]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-3">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              <img src={imgSrc(logoUrl)} alt={hotelName}
                className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2" style={{ borderColor: RED }} />
            ) : (
              <span className="w-9 h-9 rounded-full border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: RED }}>
                <span className="w-3 h-3 rounded-full" style={{ background: RED }} />
              </span>
            )}
            <span className="font-serif text-lg sm:text-xl tracking-[0.12em] uppercase truncate">{hotelName}</span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)}
                className="text-sm tracking-[0.08em] text-stone-600 hover:text-stone-900 transition-colors">
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
            {!bookingDisabled && (
              <a href={bookHref} className="hidden sm:inline px-6 py-2.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase text-white transition-opacity hover:opacity-90"
                style={{ background: RED }}>
                Book
              </a>
            )}
            <button onClick={() => setMenuOpen(v => !v)} className="lg:hidden w-11 h-11 flex items-center justify-center">
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        <DottedRule />
        {menuOpen && (
          <div className="lg:hidden bg-[#F6F3EC] px-4 py-3 space-y-1">
            {NAV_LINKS.map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="w-full text-left px-3 py-3 text-sm tracking-[0.08em] text-stone-700 hover:bg-black/5 rounded-lg">
                {label}
              </button>
            ))}
            <a href="/login" className="block px-3 py-3 text-sm text-stone-500 hover:bg-black/5 rounded-lg">Admin</a>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden" style={{ background: GREEN }}>
        {heroPhoto && (
          <img src={heroPhoto} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(105deg, ${GREEN}F2 0%, ${GREEN}B8 55%, ${GREEN}80 100%)` }} />

        <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 py-20">
          <div className="max-w-2xl">
            {address && <Eyebrow light>{address}</Eyebrow>}
            <p className="font-serif italic text-white/75 text-xl sm:text-2xl mt-6">Swagatam — you are most welcome</p>
            <h1 className="font-serif text-white leading-[0.95] mt-3">
              <span className="block text-5xl sm:text-7xl">{hotelName}</span>
            </h1>
            {aboutText && (
              <p className="text-white/80 text-base sm:text-lg leading-relaxed mt-7 max-w-lg line-clamp-3">{aboutText}</p>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-9">
              {!bookingDisabled && (
                <a href={bookHref} className="flex items-center gap-2 px-8 py-4 rounded-full text-xs font-semibold tracking-[0.18em] uppercase text-white transition-opacity hover:opacity-90"
                  style={{ background: RED }}>
                  Book Your Stay <ArrowRight size={15} />
                </a>
              )}
              <button onClick={() => scrollTo("rooms")}
                className="px-8 py-4 rounded-full text-xs font-semibold tracking-[0.18em] uppercase text-white border border-white/40 hover:bg-white/10 transition-colors">
                View Rooms
              </button>
            </div>

            {/* Availability panel */}
            {!bookingDisabled && (
              <div className="mt-12 max-w-lg rounded-2xl border border-white/15 p-6 backdrop-blur-md" style={{ background: "rgba(0,0,0,0.28)" }}>
                <span className="inline-block w-12 h-[3px] mb-4"
                  style={{ backgroundImage: `repeating-linear-gradient(to right, ${RED} 0 5px, transparent 5px 10px)` }} />
                <h2 className="font-serif text-2xl text-white mb-5">Check availability</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-white/50 mb-1.5">Check in</label>
                    <input type="date" value={checkIn} min={toLocalISODate(new Date())} onChange={e => setCheckIn(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-3 text-sm text-white focus:outline-none focus:border-white/50" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-white/50 mb-1.5">Check out</label>
                    <input type="date" value={checkOut} min={checkIn || toLocalISODate(new Date())} onChange={e => setCheckOut(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-3 text-sm text-white focus:outline-none focus:border-white/50" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-white/50 mb-1.5">Guests</label>
                    <select value={guests} onChange={e => setGuests(Number(e.target.value))}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-3 text-sm text-white focus:outline-none focus:border-white/50">
                      {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n} className="text-stone-900">{n} guest{n !== 1 ? "s" : ""}</option>)}
                    </select>
                  </div>
                </div>
                <a href={bookHref} className="inline-block mt-5 px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.18em] uppercase text-white transition-opacity hover:opacity-90"
                  style={{ background: RED }}>
                  Enquire
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── About ── */}
      {aboutText && (
        <section id="about" className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
          <div className="flex justify-center"><Eyebrow>Our Story</Eyebrow></div>
          <h2 className="font-serif text-4xl mt-5 mb-6">Why {hotelName}</h2>
          <p className="text-stone-600 leading-relaxed whitespace-pre-line">{aboutText}</p>
        </section>
      )}

      {/* ── Rooms ── */}
      <section id="rooms" style={{ background: GREEN }} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-center mb-3"><Eyebrow light>Stay With Us</Eyebrow></div>
          <h2 className="font-serif text-4xl sm:text-5xl text-white text-center mb-12">Rooms</h2>

          {bookingDisabled ? (
            <p className="text-center text-white/60 max-w-md mx-auto">
              Online booking is currently unavailable — please call {phone || "us"} to book directly.
            </p>
          ) : rooms.length === 0 ? (
            <p className="text-center text-white/50">Rooms coming soon</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.map(room => {
                const photos = (room.photos || []).map(imgSrc).filter(Boolean);
                return (
                  <article key={room._id} className="group rounded-2xl overflow-hidden border border-white/10" style={{ background: GREEN_SOFT }}>
                    <div className="relative h-52 overflow-hidden" style={{ background: GREEN }}>
                      {photos.length ? <RoomPhotoSlideshow photos={photos} alt={room.name} dotColor={RED} />
                        : <div className="w-full h-full flex items-center justify-center text-white/20"><BedDouble size={38} /></div>}
                      <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] text-white/90 bg-black/50 backdrop-blur">
                        {room.baseOccupancy || room.capacity || 2} Guests
                      </span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif text-2xl text-white">{room.name}</h3>
                      {room.description && <p className="text-white/50 text-sm mt-2 line-clamp-2">{room.description}</p>}
                      {(room.amenities || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-4">
                          {room.amenities.slice(0, 6).map(a => (
                            <span key={a} className="px-2.5 py-1 rounded-full border border-white/20 text-[11px] text-white/70">{a}</span>
                          ))}
                        </div>
                      )}
                      <div className="border-t border-white/10 mt-5 pt-4 flex items-end justify-between gap-3">
                        <div>
                          <p className="font-serif text-3xl text-white leading-none">₹{(room.pricePerNight || 0).toLocaleString("en-IN")}</p>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 mt-1.5">/ Night</p>
                        </div>
                        <div className="flex gap-2">
                          <a href={`/rooms/${room._id}`}
                            className="px-4 py-2.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.12em] text-white border border-white/25 hover:bg-white/10 transition-colors">
                            Details
                          </a>
                          <a href={`${bookHref}${bookHref.includes("?") ? "&" : "?"}roomId=${room._id}`}
                            className="px-4 py-2.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.12em] text-white transition-opacity hover:opacity-90"
                            style={{ background: RED }}>
                            Book
                          </a>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── Gallery ── */}
      <section id="gallery" className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="flex justify-center mb-3"><Eyebrow>Photo Tour</Eyebrow></div>
        <h2 className="font-serif text-4xl text-center mb-10">Gallery</h2>
        {gallery.length === 0 ? (
          <p className="text-center text-stone-400">Photos coming soon</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {gallery.map((src, i) => (
              <button key={src + i} onClick={() => setLightbox(src)} className="relative aspect-[4/3] overflow-hidden rounded-xl group">
                <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ── Reviews ── */}
      <section id="amenities" className="py-20" style={{ background: "#EFEAE0" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-center mb-3"><Eyebrow>Guest Voices</Eyebrow></div>
          <h2 className="font-serif text-4xl text-center mb-3">Reviews</h2>
          {avgRating && (
            <p className="text-center text-stone-500 text-sm mb-10">
              {avgRating} · {liveReviews.length} review{liveReviews.length !== 1 ? "s" : ""}
            </p>
          )}
          {liveReviews.length === 0 ? (
            <p className="text-center text-stone-400 mt-8">No reviews yet — be the first to stay and share your experience.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
              {liveReviews.slice(0, 6).map((r, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-stone-200/60">
                  <StarRow n={r.rating} filledClass="text-red-700 fill-red-700" emptyClass="text-stone-200 fill-stone-200" />
                  <p className="text-stone-600 text-sm mt-4 leading-relaxed">{r.text}</p>
                  <p className="font-serif text-lg mt-5">{r.name}</p>
                  <p className="text-xs text-stone-400">{r.date} · {r.via}</p>
                </div>
              ))}
            </div>
          )}
          <div className="text-center mt-10">
            <button onClick={() => setShowReviewLookup(true)}
              className="px-7 py-3.5 rounded-full text-xs font-semibold uppercase tracking-[0.18em] text-white transition-opacity hover:opacity-90"
              style={{ background: RED }}>
              Write a Review
            </button>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      {faqs && faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
          <div className="flex justify-center mb-3"><Eyebrow>Good to Know</Eyebrow></div>
          <h2 className="font-serif text-4xl text-center mb-10">Questions</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={f._id || i} className="border-b border-stone-300/60">
                <button onClick={() => setFaqOpen(faqOpen === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-5 text-left">
                  <span className="font-serif text-lg">{f.question}</span>
                  <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} style={{ color: RED }} />
                </button>
                {faqOpen === i && <p className="pb-5 text-stone-600 leading-relaxed">{f.answer}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Cross-promo ── */}
      {crossPromoName && crossPromoUrl && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
          <div className="rounded-2xl border border-stone-300/60 p-8 text-center bg-white">
            <div className="flex justify-center mb-3"><Eyebrow>Partner Service</Eyebrow></div>
            <h3 className="font-serif text-2xl mb-3">Planning a trip? Try {crossPromoName}</h3>
            <a href={crossPromoUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 px-7 py-3.5 rounded-full text-xs font-semibold uppercase tracking-[0.18em] text-white transition-opacity hover:opacity-90"
              style={{ background: RED }}>
              Learn More <ArrowRight size={14} />
            </a>
          </div>
        </section>
      )}

      {/* ── Contact ── */}
      <section id="contact" className="py-20" style={{ background: GREEN }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <Eyebrow light>Contact &amp; Location</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-white mt-5 mb-10">Come stay with us</h2>
            <div className="space-y-6">
              {address && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1.5">Address</p>
                  <p className="text-white/90">{address}</p>
                </div>
              )}
              {phone && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1.5">Phone</p>
                  <a href={`tel:${phone}`} className="text-white/90 hover:text-white">{phone}</a>
                  {phone2 && <a href={`tel:${phone2}`} className="block text-white/90 hover:text-white mt-1">{phone2}</a>}
                </div>
              )}
              {email && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1.5">Email</p>
                  <a href={`mailto:${email}`} className="text-white/90 hover:text-white">{email}</a>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-9">
              {waHref && (
                <a href={waHref} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-7 py-3.5 rounded-full text-xs font-semibold uppercase tracking-[0.18em] text-white transition-opacity hover:opacity-90"
                  style={{ background: RED }}>
                  <MessageCircle size={15} /> WhatsApp Us
                </a>
              )}
              {googleReviewUrl && (
                <a href={googleReviewUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-7 py-3.5 rounded-full text-xs font-semibold uppercase tracking-[0.18em] text-white border border-white/30 hover:bg-white/10 transition-colors">
                  <Star size={14} /> Review Us
                </a>
              )}
            </div>
          </div>

          {((mapsLat && mapsLng) || mapsUrl) && (
            <div>
              <DottedRule className="rounded-t" />
              <div className="rounded-b-2xl overflow-hidden border border-white/10 border-t-0">
                {mapsLat && mapsLng && (
                  <iframe title="Map" className="w-full h-80 border-0" loading="lazy"
                    src={`https://maps.google.com/maps?q=${mapsLat},${mapsLng}&z=15&output=embed`} />
                )}
              </div>
              <a href={mapsUrl || `https://maps.google.com/?q=${mapsLat},${mapsLng}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 mt-4 text-sm text-white/70 hover:text-white">
                <MapPin size={14} /> View on Google Maps <ExternalLink size={12} />
              </a>
            </div>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-[#F6F3EC]">
        <DottedRule />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-serif tracking-[0.12em] uppercase text-sm">{hotelName}</p>
          <div className="flex items-center gap-6 text-xs text-stone-500">
            {NAV_LINKS.slice(0, 3).map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="hover:text-stone-800">{label}</button>
            ))}
            <a href="/login" className="hover:text-stone-800">Admin</a>
          </div>
          <p className="text-xs text-stone-400">© {new Date().getFullYear()}</p>
        </div>
      </footer>

      {/* ── Mobile sticky bar ── */}
      {!bookingDisabled && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#F6F3EC]/95 backdrop-blur border-t border-stone-300/60 px-4 py-3 flex items-center gap-3 safe-bottom">
          {waHref && (
            <a href={waHref} target="_blank" rel="noopener noreferrer"
              className="w-12 h-12 rounded-full border border-stone-300 flex items-center justify-center flex-shrink-0">
              <Phone size={18} className="text-stone-700" />
            </a>
          )}
          <a href={bookHref} className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full text-xs font-semibold uppercase tracking-[0.18em] text-white"
            style={{ background: RED }}>
            Book Your Stay <ArrowRight size={15} />
          </a>
        </div>
      )}

      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="hidden sm:flex fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-white border border-stone-300 items-center justify-center shadow-lg hover:bg-stone-50">
          <ArrowUp size={18} className="text-stone-700" />
        </button>
      )}

      {lightbox && (
        <button onClick={() => setLightbox(null)} className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4">
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-xl" />
          <span className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 flex items-center justify-center text-white"><X size={20} /></span>
        </button>
      )}

      {showReviewLookup && (
        <ReviewLookupModal onClose={() => setShowReviewLookup(false)} accent={{
          focusRing: "focus:ring-red-700/40 focus:border-red-700",
          ghostBtn: "border-stone-300 text-stone-800 hover:border-red-700 hover:text-red-700",
          primaryBtn: "bg-red-700 text-white hover:bg-red-800",
        }} />
      )}
    </div>
  );
}
